import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Trash, Check, X, Archive, Download, Upload, FilePdf } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { CopyButton } from '@/components/ui/copy-button'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { useApp } from '@/contexts/AppContext'
import { getOpenQuestionCount, isThreadOpen } from '@/contexts/AppContext'
import { useGlobalHotkeys } from '@/hooks/useGlobalHotkeys'
import { useDebounce } from '@/hooks/useDebounce'
import { cn } from '@/lib/utils'
import { storage } from '@/lib/storage'
import { useDialog } from '@/contexts/DialogContext'
import { exportAllThreadsToPDF, exportAllEntitiesToZip } from '@/lib/pdf-export'
import { ArrowLeftIcon } from 'lucide-react'

export function EntitiesPage() {
  const { state, dispatch } = useApp()
  const { t } = useTranslation()
  const dialog = useDialog()
  const navigate = useNavigate()
  const [isCreating, setIsCreating] = useState(false)
  const [newEntityName, setNewEntityName] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOrder, setSortOrder] = useState<'none' | 'asc' | 'desc'>('none')
  const [isGridMode, setIsGridMode] = useState(false)
  const importInputRef = useRef<HTMLInputElement>(null)
  const gridPrefLoadedRef = useRef(false)
  
  // Load saved grid mode preference
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const saved = await storage.getPreference('ui.gridMode')
        if (!cancelled && typeof saved === 'boolean') {
          setIsGridMode(saved)
        }
      } finally {
        if (!cancelled) gridPrefLoadedRef.current = true
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Persist grid mode preference when it changes (after initial load)
  useEffect(() => {
    if (!gridPrefLoadedRef.current) return
    storage.savePreference('ui.gridMode', isGridMode)
  }, [isGridMode])
  
  const debouncedSearchQuery = useDebounce(searchQuery, 150)
  
  // Filter to show only non-archived entities
  const nonArchivedEntities = Object.values(state.entities).filter(entity => !entity.isArchived)
  
  // Apply search filter
  let filteredEntities = nonArchivedEntities.filter(entity =>
    entity.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
  )
  if (sortOrder !== 'none') {
    filteredEntities = [...filteredEntities].sort((a, b) =>
      sortOrder === 'asc'
        ? a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        : b.name.localeCompare(a.name, undefined, { sensitivity: 'base' })
    )
  }

  const handleCreateEntity = () => {
    if (newEntityName.trim()) {
      dispatch({
        type: 'CREATE_ENTITY',
        payload: {
          name: newEntityName.trim()
        }
      })
      setNewEntityName('')
      setIsCreating(false)
    }
  }

  const handleCreateEntityShortcut = () => {
    setIsCreating(true)
  }

  const handleImportClick = () => {
    importInputRef.current?.click()
  }

  const handleImportEntityShortcut = () => {
    handleImportClick()
  }

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    try {
      const text = await file.text()
      const parsed = JSON.parse(text)

      const hasStructuredPayload = parsed && typeof parsed === 'object' && 'entity' in parsed
      const rawEntity = hasStructuredPayload ? parsed.entity : parsed
      const rawThreads = hasStructuredPayload ? parsed.threads : parsed?.threads
      const rawQANodes = hasStructuredPayload ? parsed.qaNodes : parsed?.qaNodes

      if (!rawEntity || typeof rawEntity !== 'object' || !rawEntity.name) {
        throw new Error('Invalid entity structure')
      }

      const normalizedThreads = Array.isArray(rawThreads) ? rawThreads : []
      const normalizedQANodes = rawQANodes && typeof rawQANodes === 'object' ? rawQANodes : {}

      dispatch({
        type: 'IMPORT_ENTITY',
        payload: {
          entity: { ...rawEntity },
          threads: normalizedThreads.map(thread => ({ ...thread })),
          qaNodes: { ...normalizedQANodes }
        }
      } as any)
      await dialog.alert({ message: t('importEntitySuccess') })
    } catch (error) {
      console.error('Import entity failed:', error)
      await dialog.error({ message: t('importEntityError') })
    } finally {
      event.target.value = ''
    }
  }

  const handleExportAllEntities = async () => {
    try {
      const entitiesToExport = Object.values(state.entities).filter(entity => !entity.isArchived)
      
      if (entitiesToExport.length === 0) {
        await dialog.alert({ message: t('noEntitiesToExport') || 'No entities to export' })
        return
      }
      
      // Check if any entity has threads
      const hasThreads = entitiesToExport.some(entity => entity.threadIds.length > 0)
      if (!hasThreads) {
        await dialog.alert({ message: t('noThreadsToExport') || 'No threads to export' })
        return
      }
      
      await exportAllEntitiesToZip({
        entities: entitiesToExport,
        threads: state.threads,
        qaNodes: state.qaNodes
      })
    } catch (error) {
      console.error('Export all entities failed:', error)
      await dialog.error({ message: t('exportError') || 'Failed to export all entities' })
    }
  }

  // Setup keyboard shortcuts
  useGlobalHotkeys({
    shortcuts: [
      {
        key: 'a',
        modifiers: {},
        description: t('shortcutCreateEntity'),
        action: handleCreateEntityShortcut
      },
      {
        key: 'z',
        modifiers: {},
        description: t('shortcutImportEntity'),
        action: handleImportEntityShortcut
      }
    ],
    enabled: state.settings?.shortcutsEnabled ?? true
  })

  // While creating a new entity, capture Ctrl+S (save) and Ctrl+D (cancel), even if focus is inside the input
  useEffect(() => {
    const shortcutsEnabled = state.settings?.shortcutsEnabled ?? true
    if (!shortcutsEnabled) return
    if (!isCreating) return

    const handler = (e: KeyboardEvent) => {
      const key = (e.key || '').toLowerCase()
      const isKeyS = key === 's' || e.code === 'KeyS'
      const isKeyD = key === 'd' || e.code === 'KeyD'
      const isSaveCombo = isKeyS && (e.ctrlKey || e.metaKey)
      const isCancelCombo = isKeyD && (e.ctrlKey || e.metaKey)
      if (!isSaveCombo && !isCancelCombo) return
      e.preventDefault()
      e.stopPropagation()
      if (isSaveCombo) {
        handleCreateEntity()
      } else if (isCancelCombo) {
        setIsCreating(false)
        setNewEntityName('')
      }
    }
    window.addEventListener('keydown', handler, true)
    document.addEventListener('keydown', handler, true)
    return () => {
      window.removeEventListener('keydown', handler, true)
      document.removeEventListener('keydown', handler, true)
    }
  }, [isCreating, newEntityName, state.settings?.shortcutsEnabled])

  // Inform Sidebar that Ctrl+S/D are available while creating a new entity
  useEffect(() => {
    const evt = new CustomEvent('entities-create-mode-change', { detail: { creating: isCreating } })
    window.dispatchEvent(evt)
  }, [isCreating])

  return (
    <div className="p-6">
      {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="lg" className="h-10 w-10 p-0" aria-label={t('back')} onClick={() => {
            if (window.history.length > 1) { navigate(-1) } else { navigate('/', { replace: true }) }
          }}>
            <ArrowLeftIcon className="size-7" />
          </Button>
          <h1 className="text-lg font-semibold">{t('entities')}</h1>
            <Button size="sm" className="bg-success text-success-foreground hover:bg-success/90" onClick={() => setIsCreating(true)}>
              <Plus size={14} className="mr-1" />
              {t('newEntity')}
            </Button>
            <Button size="sm" variant="outline" onClick={handleImportClick}>
              <Upload size={14} className="mr-1" />
              {t('importEntity')}
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              className="text-pdf-export hover:text-pdf-export-hover hover:bg-pdf-export/10"
              onClick={handleExportAllEntities}
              disabled={nonArchivedEntities.length === 0}
              aria-label={t('exportAllEntities')}
            >
              <FilePdf size={16} className="mr-2" />
              {t('exportAllEntities')}
            </Button>
      </div>

      {/* Search + Sort + View toggles */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          type="text"
          placeholder={t('searchEntities')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-md"
        />
        <Button
          variant="outline"
          onClick={() => setSortOrder(prev => prev === 'none' ? 'asc' : prev === 'asc' ? 'desc' : 'none')}
          title={`Sort: ${sortOrder}`}
        >
          {sortOrder === 'none' ? 'A↕' : sortOrder === 'asc' ? 'A→Z' : 'Z→A'}
        </Button>
        <div className="flex items-center gap-2">
          <Checkbox 
            id="grid-mode"
            checked={isGridMode}
            onCheckedChange={(checked) => setIsGridMode(Boolean(checked))}
          />
          <Label htmlFor="grid-mode" className="text-sm cursor-pointer">
            {t('gridMode')}
          </Label>
        </div>
      </div>

      {/* Inline create row */}
      {isCreating && (
        <div className="mb-3">
          <Card className="p-4 border-success-border bg-success-soft">
            <div className="space-y-2" onClick={(e) => { e.preventDefault(); e.stopPropagation() }}>
              <Label htmlFor="new-entity">{t('name')}</Label>
              <div className="flex items-center gap-2">
                <CopyButton value={newEntityName || ''} title={t('copy') || 'Copy'} />
                <Input
                  id="new-entity"
                  value={newEntityName}
                  onChange={(e) => setNewEntityName(e.target.value)}
                  placeholder={t('entityName')}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) { e.preventDefault(); handleCreateEntity() }
                    else if (e.key === 'Escape') { e.preventDefault(); setIsCreating(false); setNewEntityName('') }
                  }}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button className="bg-success text-success-foreground hover:bg-success/90" onClick={handleCreateEntity}>{t('create')}</Button>
                <Button className="bg-error text-error-foreground hover:bg-error/90" onClick={() => { setIsCreating(false); setNewEntityName('') }}>{t('cancel')}</Button>
              </div>
              {/* Shortcut hints moved to Sidebar only */}
            </div>
          </Card>
        </div>
      )}

      {/* Entities List */}
      {filteredEntities.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <p>{nonArchivedEntities.length === 0 ? t('noEntitiesYet') : 'No entities match your search.'}</p>
        </div>
      ) : (
        <div className={cn(
          isGridMode 
            ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
            : "grid gap-3"
        )}>
          {filteredEntities.map((entity) => {
            const threadCount = entity.threadIds.length
            const openQuestionCount = getOpenQuestionCount(entity.id, state)

            const hasOpenThread = state.entities[entity.id].threadIds.some((tid) => {
              const t = state.threads[tid]
              if (!t) return false
              return isThreadOpen(t.rootQuestionId, state.qaNodes)
            })
            return (
              <EntityListItem
                key={entity.id}
                entityId={entity.id}
                name={entity.name}
                hasOpenThread={hasOpenThread}
                threadCount={threadCount}
                openQuestionCount={openQuestionCount}
                isGridMode={isGridMode}
              />
            )}
          )}
        </div>
      )}
      
      <input
        ref={importInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={handleImportFile}
      />
    </div>
  )
}

interface EntityListItemProps {
  entityId: string
  name: string
  hasOpenThread: boolean
  threadCount: number
  openQuestionCount: number
  isGridMode: boolean
}

function EntityListItem({ entityId, name, hasOpenThread, threadCount, openQuestionCount, isGridMode }: EntityListItemProps) {
  const { t } = useTranslation()
  const { state, dispatch } = useApp()
  const dialog = useDialog()
  const verboseMode = state.settings?.verboseMode ?? true
  const [isEditing, setIsEditing] = useState(false)
  const [title, setTitle] = useState(name)
  const entityNameTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Move cursor to end when editing entity name
  useEffect(() => {
    if (isEditing && entityNameTextareaRef.current) {
      const textarea = entityNameTextareaRef.current
      const length = textarea.value.length
      textarea.setSelectionRange(length, length)
    }
  }, [isEditing])

  const onTitleClick: React.MouseEventHandler = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsEditing(true)
  }

  const save = () => {
    const next = title.trim()
    if (!next) return
    if (next !== name) {
      dispatch({ type: 'UPDATE_ENTITY', payload: { id: entityId, name: next } } as any)
    }
    setIsEditing(false)
  }

  const cancel = () => {
    setTitle(name)
    setIsEditing(false)
  }

  const handleKey = (e: React.KeyboardEvent) => {
    const key = (e.key || '').toLowerCase()
    const isKeyS = key === 's' || (e as any).code === 'KeyS'
    const isKeyD = key === 'd' || (e as any).code === 'KeyD'
    const isSaveCombo = isKeyS && (e.ctrlKey || (e as any).metaKey)
    const isCancelCombo = isKeyD && (e.ctrlKey || (e as any).metaKey)
    if (isSaveCombo) {
      e.preventDefault()
      e.stopPropagation()
      save()
      return
    }
    if (isCancelCombo) {
      e.preventDefault()
      e.stopPropagation()
      cancel()
      return
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
      e.preventDefault()
      save()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    }
  }

  const handleArchive = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dispatch({ type: 'ARCHIVE_ENTITY', payload: { id: entityId } } as any)
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const confirmed = await dialog.confirm({
      message: t('confirmDeleteEntity', { name }),
      type: 'warning'
    })
    if (confirmed) {
      dispatch({ type: 'DELETE_ENTITY', payload: { id: entityId } } as any)
    }
  }

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      const entity = state.entities[entityId]
      if (!entity) return
      
      // Get all related threads and QA nodes
      const relatedThreads = entity.threadIds.map(id => state.threads[id]).filter(Boolean)
      const relatedQANodes: Record<string, any> = {}
      
      relatedThreads.forEach(thread => {
        const collectQANodes = (nodeId: string) => {
          const node = state.qaNodes[nodeId]
          if (node) {
            relatedQANodes[nodeId] = node
            if (node.type === 'answer' && node.followUpQuestionIds) {
              node.followUpQuestionIds.forEach(collectQANodes)
            }
            if (node.type === 'question' && node.answerId) {
              collectQANodes(node.answerId)
            }
          }
        }
        if (thread.rootQuestionId) {
          collectQANodes(thread.rootQuestionId)
        }
      })
      
      const entityData = {
        entity,
        threads: relatedThreads,
        qaNodes: relatedQANodes
      }
      
      const blob = new Blob([JSON.stringify(entityData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      const timestamp = new Date().toISOString().replace(/[:]/g, '-').split('.')[0]
      anchor.href = url
      anchor.download = `entity-${entity.name.replace(/[^a-zA-Z0-9]/g, '_')}-${timestamp}.json`
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
    }
  }

  const handleExportAllThreads = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      const entity = state.entities[entityId]
      if (!entity) return
      
      const relatedThreads = entity.threadIds.map(id => state.threads[id]).filter(Boolean)
      
      if (relatedThreads.length === 0) {
        await dialog.alert({ message: t('noThreadsToExport') || 'No threads to export' })
        return
      }
      
      await exportAllThreadsToPDF({
        entityName: entity.name,
        threads: relatedThreads,
        qaNodes: state.qaNodes
      })
    } catch (error) {
      console.error('Export all threads failed:', error)
      await dialog.error({ message: t('exportError') || 'Failed to export all threads to PDF' })
    }
  }

  // Broadcast editing state so Sidebar can show Ctrl+S / Ctrl+D on Entities page
  useEffect(() => {
    const evt = new CustomEvent('entities-title-editing-change', { detail: { entityId, editing: isEditing } })
    window.dispatchEvent(evt)
  }, [entityId, isEditing])

  if (isGridMode) {
    return (
      <Link to={`/entities/${entityId}`}>
        <Card className={cn(
          "p-2.5 transition-colors cursor-pointer border-border h-full min-h-[8.5rem] flex flex-col gap-2.5",
          hasOpenThread ? "bg-open-question hover:bg-open-question/90" : "bg-card hover:bg-accent/10"
        )}>
          <div className="flex items-center justify-between text-xs">
            <div className="space-y-1 text-left">
              {verboseMode && (
              <div className="flex items-baseline gap-1">
                <span className="text-muted-foreground">{t('threads')}</span>
                <span className="font-semibold text-sm">{threadCount}</span>
              </div>
              )}
              {verboseMode && (
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground">{t('openQuestions')}</span>
                <Badge variant="secondary" className="text-[0.65rem] px-2 py-0.5">{openQuestionCount}</Badge>
              </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="destructive"
                size="sm"
                aria-label="Delete entity"
                onClick={handleDelete}
                className="h-7 px-2"
              >
                <Trash size={12} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                aria-label="Download entity"
                onClick={handleDownload}
                className="h-7 px-2"
              >
                <Download size={10} />
              </Button>
              <Button
                size="sm"
                onClick={handleArchive}
                className="h-7 px-2 text-xs bg-archive text-archive-foreground hover:bg-archive/90"
              >
                <Archive size={10} />
              </Button>
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center justify-center text-center">
            {isEditing ? (
              <div className="space-y-2 w-full" onClick={(e) => { e.preventDefault(); e.stopPropagation() }}>
                <div className="flex items-center gap-2 justify-center">
                  <CopyButton value={title || ''} title={t('copy') || 'Copy'} />
                  <Textarea
                    ref={entityNameTextareaRef}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="min-h-[2rem] resize-none field-sizing-content text-center"
                    onKeyDown={handleKey}
                    autoFocus
                  />
                </div>
                <div className="flex items-center gap-2 justify-center">
                  <Button size="sm" className="bg-green-600 text-white hover:bg-green-700" onClick={(e) => { e.preventDefault(); e.stopPropagation(); save() }}>
                    <Check size={12} className="mr-1" />
                    {t('save')}
                  </Button>
                  <Button size="sm" className="bg-red-600 text-white hover:bg-red-700" onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancel() }}>
                    <X size={12} className="mr-1" />
                    {t('cancel')}
                  </Button>
                </div>
                {/* Shortcut hints moved to Sidebar only */}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 w-full">
                <div className="flex items-center justify-center gap-2">
                  <CopyButton value={name || ''} title={t('copy') || 'Copy'} />
                  <h3 className="font-medium text-sm hover:bg-accent/20 px-2 py-1 rounded cursor-pointer break-words" onClick={onTitleClick}>
                    {name || <span className="text-muted-foreground italic">{t('clickToAddText')}</span>}
                  </h3>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-pdf-export hover:text-pdf-export-hover hover:bg-pdf-export/10 w-full"
                  onClick={handleExportAllThreads}
                  disabled={threadCount === 0}
                  aria-label={t('exportAll')}
                >
                  <FilePdf size={16} className="mr-2" />
                  {t('exportAll')}
                </Button>
              </div>
            )}
          </div>
        </Card>
      </Link>
    )
  }

  // List mode (original layout)
  return (
    <Link to={`/entities/${entityId}`}>
      <Card className={cn(
        "p-4 transition-colors cursor-pointer border-border",
        hasOpenThread ? "bg-open-question hover:bg-open-question/90" : "bg-card hover:bg-accent/10"
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1">
            <Button
              variant="destructive"
              size="sm"
              aria-label="Delete entity"
              onClick={handleDelete}
            >
              <Trash size={14} />
            </Button>
            <div id="as-long-as-content" className="w-fit">
              {isEditing ? (
                <div className="space-y-2" onClick={(e) => { e.preventDefault(); e.stopPropagation() }}>
                  <div className="flex items-center gap-2">
                    <CopyButton value={title || ''} title={t('copy') || 'Copy'} />
                    <Textarea
                      ref={entityNameTextareaRef}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="min-h-[2rem] resize-none field-sizing-content flex-1"
                      onKeyDown={handleKey}
                      autoFocus
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-green-600 text-white hover:bg-green-700" onClick={(e) => { e.preventDefault(); e.stopPropagation(); save() }}>
                      <Check size={12} className="mr-1" />
                      {t('save')}
                    </Button>
                    <Button size="sm" className="bg-red-600 text-white hover:bg-red-700" onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancel() }}>
                      <X size={12} className="mr-1" />
                      {t('cancel')}
                    </Button>
                  </div>
                  {/* Shortcut hints moved to Sidebar only */}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-sm hover:bg-accent/20 p-2 rounded cursor-pointer w-fit" onClick={onTitleClick}>
                    {name || <span className="text-muted-foreground italic">{t('clickToAddText')}</span>}
                  </h3>
                  <CopyButton value={name || ''} title={t('copy') || 'Copy'} />
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 ml-4">
            {verboseMode && (
            <div className="text-right">
              <div className="text-xs text-muted-foreground">{t('threads')}</div>
              <div className="text-sm font-medium">{threadCount}</div>
            </div>
            )}
            {verboseMode && openQuestionCount > 0 && (
              <div className="text-right">
                <div className="text-xs text-muted-foreground">{t('openQuestions')}</div>
                <Badge variant="secondary" className="text-xs">
                  {openQuestionCount}
                </Badge>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              className="text-pdf-export hover:text-pdf-export-hover hover:bg-pdf-export/10"
              onClick={handleExportAllThreads}
              disabled={threadCount === 0}
              aria-label={t('exportAll')}
            >
              <FilePdf size={16} className="mr-2" />
              {t('exportAll')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="text-xs"
            >
              <Download size={12} className="mr-1" />
              {t('downloadEntity')}
            </Button>
            <Button
              size="sm"
              onClick={handleArchive}
              className="text-xs bg-archive text-archive-foreground hover:bg-archive/90"
            >
              <Archive size={12} className="mr-1" />
              {t('archivia')}
            </Button>
          </div>
        </div>
      </Card>
    </Link>
  )
}